/**
 * One-time on-duty location disclosure, shown after a user's first account
 * setup and before their first clock-in. The answer is stored on their profile.
 *
 * "Not Now" is allowed — the Time Clock's shift-start action stays disabled with
 * a link back to this screen. Nothing here bypasses the browser/OS permission
 * prompt.
 */
import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import {
  OPEN_LOCATION_CONSENT_EVENT,
  useProfileLocationConsent,
} from "@/lib/location-consent-profile";

export function FirstRunLocationConsent() {
  const { user, currentOrg } = useAuth();
  const { status, agree } = useProfileLocationConsent(user?.id ?? null);
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  // First-run: shown automatically until the account has an answer.
  useEffect(() => {
    if (!user?.id || !currentOrg) { setOpen(false); return; }
    if (status !== "unset" || dismissed) { setOpen(false); return; }
    const t = window.setTimeout(() => setOpen(true), 600);
    return () => window.clearTimeout(t);
  }, [user?.id, currentOrg, status, dismissed]);

  // Re-openable from the Time Clock notice.
  useEffect(() => {
    const reopen = () => { setDismissed(false); setOpen(true); };
    window.addEventListener(OPEN_LOCATION_CONSENT_EVENT, reopen);
    return () => window.removeEventListener(OPEN_LOCATION_CONSENT_EVENT, reopen);
  }, []);

  if (!open) return null;

  const onAgree = async () => {
    setBusy(true);
    const ok = await agree();
    setBusy(false);
    if (ok) setOpen(false);
  };

  const onNotNow = () => { setDismissed(true); setOpen(false); };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mr-first-loc-consent-title"
      className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 p-4 sm:items-center"
    >
      <div className="w-full max-w-md rounded-2xl border border-[#C0C0C0]/20 bg-card p-5 shadow-2xl">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 rounded-full bg-primary/15 p-2 text-primary">
            <MapPin className="h-5 w-5" />
          </span>
          <div>
            <h2 id="mr-first-loc-consent-title" className="font-display text-lg font-bold">
              Location Tracking While On Duty
            </h2>
            <div className="mt-2 space-y-2 text-sm text-muted-foreground">
              <p>
                MissionReady collects your location to track your duty status while you
                are on shift. Tracking starts automatically when you clock in and stops
                automatically when you clock out.
              </p>
              <p>
                While you are on duty, location collection may continue when the
                MissionReady app is running in the background or your device screen is
                locked, so your dispatch team can see where you are if you need help.
              </p>
              <p>
                It is never active when you are off duty, and your position is only shared
                with your employer's dispatch team during your shift — never with other
                guards.
              </p>
              <p>
                You need to agree before you can clock in. Your phone will still ask you
                separately for permission to use location.
              </p>
            </div>
          </div>
        </div>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse">
          <Button onClick={onAgree} disabled={busy} className="sm:min-w-40">
            {busy ? "Saving…" : "Agree and Continue"}
          </Button>
          <Button variant="outline" onClick={onNotNow} disabled={busy}>Not Now</Button>
        </div>
      </div>
    </div>
  );
}
